<template>
  <InfuseModal
    :title="isUpdate ? t('iam.users.header.update_user') : t('iam.users.header.create_user')"
    @close="emit('close')"
    :disable-close="loading"
    :optional-body-classes="optionalBodyClasses"
  >
    <span v-if="optionalHeaderTooltip" class="-ml-3 -mt-6 mb-1 block cursor-help p-3 text-orange-400">
      <InfuseTooltip :content="optionalHeaderTooltip" offset-distance="8">
        <font-awesome-icon :icon="['fal', 'info-circle']" />
      </InfuseTooltip>
    </span>

    <form id="add-user" @submit.prevent="onSubmit" class="flex flex-col gap-2">
      <InfuseField :label="t('iam.users.name')">
        <InfuseInput
          :required="true"
          v-model="nameMutation"
          ref="nameInputRef"
          :placeholder="t('iam.users.name_placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('iam.users.email')">
        <InfuseInput
          :required="true"
          :pattern="validEmailAddress"
          :disabled="isUpdate"
          v-model="emailMutation"
          ref="emailInputRef"
          :placeholder="t('iam.users.email_placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('iam.users.locale')" v-if="!isUpdate">
        <LocaleSelect :available-locales="availableLocales" :required="true" v-model="localeMutation" />
      </InfuseField>

      <InfuseField v-if="canAssignRoles" :label="t('iam.users.roles')" :tooltip="rolesTooltip">
        <RoleMultiSelect v-model="rolesMutation" :required="false" :level="level" :parent-id="parentId" />
      </InfuseField>
    </form>
    <template #actions>
      <InfuseButton
        data-testid="modal-cancel-button"
        color="secondary"
        type="button"
        @click="emit('close')"
        :disabled="loading"
      >
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        form="add-user"
        :disabled="loading"
        :loading="loading"
      >
        {{ t('action.submit') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { CreateUser, UpdateUser, User } from '@infuse/iam/user/type';
import { organisationUserPermissions } from '@infuse/api/iam/organisation';
import { tenantUserPermissions } from '@infuse/api/iam/tenant';
import { userPermissions } from '@infuse/api/iam/user';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import { RoleMultiSelect } from '@infuse/iam/role/ui';
import { UserLevel } from '@infuse/iam/user/type';
import { InfuseButton, InfuseField, InfuseInput, InfuseModal, InfuseTooltip } from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLevel } from '@infuse/iam/user/utils';
import LocaleSelect from './LocaleSelect.vue';

const { t, locale, availableLocales } = useI18n();

const nameMutation = ref('');
const emailMutation = ref('');
const localeMutation = ref(locale.value);
const rolesMutation = ref<string[]>([]);

const isUpdate = computed(() => !!props.user);

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    user?: User;
    navigationLevel?: UserLevel;
    navigationParentId?: string;
    optionalBodyClasses?: string;
    optionalHeaderTooltip?: string;
    rolesTooltip?: string;
  }>(),
  {
    loading: false,
    navigationLevel: UserLevel.DOMAIN,
    navigationParentId: undefined,
    optionalBodyClasses: '',
    rolesTooltip: undefined
  }
);

const level = computed(() => {
  if (isUpdate.value && props.user) {
    // We should only allowing updating of the user in the same level as the user
    return getLevel(props.user);
  }

  // Otherwise, use the navigation level
  return props.navigationLevel;
});

const currentUserStore = useCurrentUserStore();

// Assigning roles is a separate call from creating the user. Offering the field to someone who may not
// make that call only produces a failed request after the user has already been created.
const canAssignRoles = computed(() => {
  switch (level.value) {
    case UserLevel.ORGANISATION:
      return currentUserStore.hasPermission(organisationUserPermissions.ADD_ROLES);
    case UserLevel.TENANT:
      return currentUserStore.hasPermission(tenantUserPermissions.ADD_ROLES);
    default:
      return currentUserStore.hasPermission(userPermissions.ADD_ROLES);
  }
});

const parentId = computed(() => {
  if (isUpdate.value && props.user) {
    // We should only allowing updating of the user in the same level as the user
    switch (level.value) {
      case UserLevel.ORGANISATION:
        return props.user.organisationId;
      case UserLevel.TENANT:
        return props.user.tenantId;
      default:
        return undefined;
    }
  }

  // Otherwise, use the navigation parent id
  return props.navigationParentId;
});

interface Emits {
  (event: 'close'): void;
  (event: 'create', vars: { user: CreateUser }): void;
  (event: 'update', vars: { id: string; user: UpdateUser }): void;
}

const emit = defineEmits<Emits>();

const validEmailAddress = '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';

watch(
  () => props.user,
  (user) => {
    if (!user) {
      return;
    }

    nameMutation.value = user.name;
    emailMutation.value = user.email;
    localeMutation.value = user.locale ?? 'en';
    rolesMutation.value = user.roleLabels.map((role) => role.value);
  },
  { immediate: true }
);

const onSubmit = () => {
  // Guard against double submission (e.g. Enter key while a request is already in flight)
  if (props.loading) {
    return;
  }

  const userData = {
    name: nameMutation.value,
    email: emailMutation.value,
    roles: rolesMutation.value,
    locale: localeMutation.value
  };

  // Check if this is an update (has existing user with id and version)
  if (props.user?.id && props.user?.version !== undefined) {
    // UPDATE: emit 'update' event with id and version
    emit('update', {
      id: props.user.id,
      user: {
        ...userData,
        version: props.user.version
      }
    });
  } else {
    // CREATE: emit 'create' event without id or version
    emit('create', {
      user: userData
    });
  }
};
</script>
